import './ProjectPage.scss';
import CircularProgress from '../../../shared/components/CircularProgress/CircularProgress';
import LinearProgress from '../../../shared/components/LinearProgress/LinearProgress';
import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import Alert from '../../../shared/components/Alert/Alert';
import Box from '../../../shared/components/Box/Box';
import Input from '../../../shared/components/Input/Input';
import Dialog from '../../../shared/components/Dialog/Dialog';
import Typography from '../../../shared/components/Typography/Typography';
import { createProjectNode, getOneProject, updateProject } from '../project.api';
import ProjectPlanningTraceability from '../components/ProjectPlanningTraceability';

const getValue = (source, ...keys) => {
  for (const key of keys) {
    if (source?.[key] !== undefined && source?.[key] !== null && source?.[key] !== '') {
      return source[key];
    }
  }

  return '';
};

const formatDate = (value) => {
  if (!value) return '-';

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return date.toLocaleDateString('fr-FR');
};

const formatMoney = (value) => {
  if (value === null || value === undefined || value === '') return '-';

  const amount = Number(value);
  if (Number.isNaN(amount)) {
    return value;
  }

  return new Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency: 'EUR',
    maximumFractionDigits: 0,
  }).format(amount);
};

const formatSurface = (value) => {
  if (value === null || value === undefined || value === '') return '-';

  const surface = Number(value);
  if (Number.isNaN(surface)) {
    return value;
  }

  return `${surface.toLocaleString('fr-FR')} m2`;
};

const clampProgress = (value) => {
  const progress = Number(value);
  if (Number.isNaN(progress)) return 0;

  return Math.min(100, Math.max(0, progress));
};

const getClient = (project) => project?.Client || project?.client || project?.company || null;

const getClientName = (client) => getValue(client, 'socialReason', 'social_reason', 'name', 'legalName', 'legal_name');

const getSelectedLabels = (values = {}, labels = {}) => Object.entries(values)
  .filter(([, selected]) => selected === true)
  .map(([key]) => labels[key] || key);

const phaseLabels = {
  chiffrage: 'Chiffrage',
  Chiffrage: 'Chiffrage',
  conception: 'Conception',
  Conception: 'Conception',
  execution: 'Execution',
  EXE: 'EXE',
  doe: 'DOE',
  DOE: 'DOE',
};

const managementLabels = {
  reunionChantier: 'Réunion chantier',
  reunionSynthese: 'Réunion synthese',
  pointsClients: 'Entretien client',
  recetteUsine: 'Recette usine',
};

const phaseOptions = ['Chiffrage', 'Conception', 'EXE', 'DOE'];
const managementOptions = Object.entries(managementLabels);

const projectStatuses = ['Brouillon', 'En cours', 'En attente', 'Terminé', 'Archivé'];

const projectTypeOptions = ['Tertiaire', 'Industrie', 'Traction', 'Nucléaire', 'Logement'];

const toDateInputValue = (value) => {
  if (!value) return '';

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return String(value).slice(0, 10);
  }

  return date.toISOString().slice(0, 10);
};

const normalizeProjectPhases = (project = {}) => {
  const values = project.phases || project.phases_json || {};
  return {
    Chiffrage: Boolean(values.Chiffrage ?? values.chiffrage),
    Conception: Boolean(values.Conception ?? values.conception),
    EXE: Boolean(values.EXE ?? values.execution),
    DOE: Boolean(values.DOE ?? values.doe),
  };
};

const buildProjectForm = (project = {}) => ({
  code: project.code || '',
  name: project.name || '',
  label: project.label || '',
  description: project.description || '',
  projectType: getValue(project, 'projectType', 'project_type'),
  status: project.status || 'Brouillon',
  startDate: toDateInputValue(getValue(project, 'startDate', 'start_date')),
  endDate: toDateInputValue(getValue(project, 'endDate', 'end_date')),
  budget: project.budget ?? '',
  soldAmount: getValue(project, 'soldAmount', 'sold_amount'),
  estimatedCost: getValue(project, 'estimatedCost', 'estimated_cost'),
  progressPercent: getValue(project, 'progressPercent', 'progress_percent'),
  address_line1: getValue(project, 'address_line1', 'addressLine1'),
  address_line2: getValue(project, 'address_line2', 'addressLine2'),
  city: project.city || '',
  region_or_state: getValue(project, 'region_or_state', 'regionOrState'),
  postal_code: getValue(project, 'postal_code', 'postalCode'),
  country_code: getValue(project, 'country_code', 'countryCode') || 'FR',
  buildingType: getValue(project, 'buildingType', 'building_type'),
  surface: project.surface ?? '',
  workType: getValue(project, 'workType', 'work_type'),
  phases: normalizeProjectPhases(project),
  management: { ...(project.management || project.management_json || {}) },
});

const FieldRow = ({ label, value }) => (
  <div className="project-field-row">
    <dt>{label}</dt>
    <dd>{value || '-'}</dd>
  </div>
);

const SummaryItem = ({ label, value }) => (
  <div className="project-summary-item">
    <span>{label}</span>
    <strong>{value || '-'}</strong>
  </div>
);

function ProjectPage({ forcedRealizationType = null }) {
  const { id } = useParams();
  const projectId = id;
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  const [projectForm, setProjectForm] = useState(buildProjectForm({}));
  const [modalError, setModalError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [nodeForm, setNodeForm] = useState({ code: '', name: '', nodeType: 'custom', parentNodeId: '' });
  const [addingNode, setAddingNode] = useState(false);

  useEffect(() => {
    const fetchProject = async () => {
      if (!projectId) return;

      try {
        setLoading(true);
        const data = await getOneProject(projectId);
        setProject(data);
        setError(null);
      } catch (err) {
        setError(err.message || 'Impossible de charger la réalisation');
        setProject(null);
      } finally {
        setLoading(false);
      }
    };

    fetchProject();
  }, [projectId]);

  const client = getClient(project);
  const progress = clampProgress(getValue(project, 'progressPercent', 'progress_percent'));
  const phases = useMemo(() => project?.phases || project?.phases_json || {}, [project]);
  const management = useMemo(() => project?.management || project?.management_json || {}, [project]);
  const complements = project?.complements || project?.complements_json || {};
  const selectedPhases = useMemo(() => getSelectedLabels(phases, phaseLabels), [phases]);
  const selectedManagement = useMemo(() => getSelectedLabels(management, managementLabels), [management]);
  const isIntervention = forcedRealizationType === 'intervention' || project?.realizationType === 'intervention' || project?.realization_type === 'intervention';
  const realizationLabel = isIntervention ? 'Intervention' : 'Projet';
  const realizationBasePath = isIntervention ? '/interventions' : '/projets';
  const projectName = getValue(project, 'name', 'label') || realizationLabel;
  const site = [getValue(project, 'postal_code', 'postalCode'), getValue(project, 'city')].filter(Boolean).join(' ');
  const nodes = project?.nodes || project?.ProjectNodes || [];

  const handleAddNode = async () => {
    if (!nodeForm.code.trim() || !nodeForm.name.trim()) return;
    try {
      setAddingNode(true);
      await createProjectNode(projectId, nodeForm);
      setProject(await getOneProject(projectId));
      setNodeForm({ code: '', name: '', nodeType: 'custom', parentNodeId: '' });
    } catch (err) {
      setError(err.message || 'Impossible de créer le nœud');
    } finally {
      setAddingNode(false);
    }
  };

  const openEditModal = () => {
    setProjectForm(buildProjectForm(project || {}));
    setModalError(null);
    setEditOpen(true);
  };

  const closeEditModal = () => {
    if (saving) return;
    setEditOpen(false);
    setModalError(null);
  };

  const updateProjectField = (event) => {
    const { name, value } = event.target;
    const nextValue = name === 'country_code' ? value.toUpperCase().slice(0, 2) : value;
    setProjectForm((current) => ({ ...current, [name]: nextValue }));
  };
  const toggleProjectOption = (group, key) => setProjectForm((current) => ({
    ...current,
    [group]: { ...current[group], [key]: !current[group]?.[key] },
  }));

  const buildUpdatePayload = () => ({
    ...projectForm,
    projectType: projectForm.projectType || null,
    startDate: projectForm.startDate || null,
    endDate: projectForm.endDate || null,
    budget: projectForm.budget === '' ? null : projectForm.budget,
    soldAmount: projectForm.soldAmount === '' ? null : projectForm.soldAmount,
    estimatedCost: projectForm.estimatedCost === '' ? null : projectForm.estimatedCost,
    progressPercent: projectForm.progressPercent === '' ? null : projectForm.progressPercent,
    surface: projectForm.surface === '' ? null : projectForm.surface,
  });

  const handleSaveProject = async () => {
    if (!projectForm.name.trim()) {
      setModalError('Le nom de la réalisation est obligatoire.');
      return;
    }

    if (!projectForm.address_line1.trim()) {
      setModalError('L adresse ligne 1 est obligatoire.');
      return;
    }

    try {
      setSaving(true);
      setModalError(null);
      const updatedProject = await updateProject(projectId, buildUpdatePayload());
      setProject(updatedProject);
      setEditOpen(false);
    } catch (err) {
      setModalError(err.message || 'Impossible de modifier la réalisation');
    } finally {
      setSaving(false);
    }
  };

  const renderEditModal = () => (
    <Dialog
      open={editOpen}
      onClose={closeEditModal}
      title={`Modifier ${isIntervention ? "l’intervention" : "le projet"}`}
      onSubmit={handleSaveProject}
      submitLabel="Enregistrer"
      loading={saving}
    >
      {modalError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {modalError}
        </Alert>
      )}

      <div className="project-creation-grid">
        <Input fullWidth label={`Numéro ${isIntervention ? "d’intervention" : "de projet"}`} name="code" value={projectForm.code} disabled />
        <Input fullWidth required label={`Nom ${isIntervention ? "de l’intervention" : "du projet"}`} name="name" value={projectForm.name} onChange={updateProjectField} />
        <Input fullWidth label={`Libellé ${isIntervention ? "de l’intervention" : "du projet"}`} name="label" value={projectForm.label} onChange={updateProjectField} />
        <div className="custom-input custom-input--full">
          <label className="custom-input__label" htmlFor="project-type">
            Type
          </label>
          <select
            id="project-type"
            className="custom-input__field"
            name="projectType"
            value={projectForm.projectType}
            onChange={updateProjectField}
          >
            <option value="">Selectionner</option>
            {projectTypeOptions.map((type) => (
              <option key={type} value={type}>
                {type}
              </option>
            ))}
          </select>
        </div>
        <div className="custom-input custom-input--full">
          <label className="custom-input__label" htmlFor="project-status">
            Statut
          </label>
          <select
            id="project-status"
            className="custom-input__field"
            name="status"
            value={projectForm.status}
            onChange={updateProjectField}
          >
            {projectStatuses.map((status) => (
              <option key={status} value={status}>
                {status}
              </option>
            ))}
          </select>
        </div>
        <Input fullWidth multiline rows={4} label={`Objet ${isIntervention ? "de l’intervention" : "du projet"}`} name="description" value={projectForm.description} onChange={updateProjectField} />
        <Input fullWidth label="Date debut" name="startDate" type="date" value={projectForm.startDate} onChange={updateProjectField} />
        <Input fullWidth label="Date fin" name="endDate" type="date" value={projectForm.endDate} onChange={updateProjectField} />
        <Input fullWidth label="Avancement (%)" name="progressPercent" type="number" value={projectForm.progressPercent} onChange={updateProjectField} inputProps={{ min: 0, max: 100 }} />
        <Input fullWidth label="Budget" name="budget" type="number" value={projectForm.budget} onChange={updateProjectField} />
        <Input fullWidth label="Montant vendu" name="soldAmount" type="number" value={projectForm.soldAmount} onChange={updateProjectField} />
        <Input fullWidth label="Cout estime" name="estimatedCost" type="number" value={projectForm.estimatedCost} onChange={updateProjectField} />
        <Input fullWidth required label="Adresse ligne 1" name="address_line1" value={projectForm.address_line1} onChange={updateProjectField} />
        <Input fullWidth label="Adresse ligne 2" name="address_line2" value={projectForm.address_line2} onChange={updateProjectField} />
        <Input fullWidth label="Ville" name="city" value={projectForm.city} onChange={updateProjectField} />
        <Input fullWidth label="Region / Etat" name="region_or_state" value={projectForm.region_or_state} onChange={updateProjectField} />
        <Input fullWidth label="Code postal" name="postal_code" value={projectForm.postal_code} onChange={updateProjectField} />
        <Input fullWidth required label="Code pays" name="country_code" value={projectForm.country_code} onChange={updateProjectField} inputProps={{ maxLength: 2 }} />
        <Input fullWidth label="Type de batiment" name="buildingType" value={projectForm.buildingType} onChange={updateProjectField} />
        <Input fullWidth label="Surface" name="surface" type="number" value={projectForm.surface} onChange={updateProjectField} />
        <Input fullWidth label="Type de travaux" name="workType" value={projectForm.workType} onChange={updateProjectField} />
        <div className="affair-conversion-options"><fieldset><legend>Phases</legend>{phaseOptions.map((phase) => <label key={phase}><input type="checkbox" checked={Boolean(projectForm.phases?.[phase])} onChange={() => toggleProjectOption('phases', phase)} /> {phase}</label>)}</fieldset><fieldset><legend>Gestion</legend>{managementOptions.map(([key, label]) => <label key={key}><input type="checkbox" checked={Boolean(projectForm.management?.[key])} onChange={() => toggleProjectOption('management', key)} /> {label}</label>)}</fieldset></div>
      </div>
    </Dialog>
  );

  // Conservés pour la section périmètres actuellement masquée dans le JSX.
  void addingNode;
  void nodes;
  void handleAddNode;

  return (
    <Box className="page-container module-page project-page">
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}>
          <CircularProgress />
        </Box>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {project && (
        <>
          <header className="project-page-header">
            <div>
              <Typography variant="h4">{projectName}</Typography>
              <div className="project-page-meta">
                <span>{project.code || `${realizationLabel} ${project.id}`}</span>
                <span>{project.status || 'Statut non renseigne'}</span>
              </div>
            </div>

            <div className="project-page-actions">
              <button className="basic-button active" type="button" onClick={openEditModal}>
                Modifier
              </button>
              <Link className="basic-button" to={`/saisietemps/${projectId}`}>
                Pointer
              </Link>
              <Link className="basic-button" to={`${realizationBasePath}/${projectId}/lda`}>
                LDA
              </Link>
              <Link className="basic-button" to={`/projets/${projectId}/devis`}>
                Devis
              </Link>
              <Link className="basic-button" to={realizationBasePath}>
                Retour
              </Link>
            </div>
          </header>

          <section className="project-summary-band">
            <SummaryItem label="Libelle" value={project.label} />
            <SummaryItem label="Type" value={isIntervention ? 'Intervention' : getValue(project, 'projectType', 'project_type')} />
            <SummaryItem label="Client" value={getClientName(client)} />
            <SummaryItem label="Site" value={site} />
          </section>

          <div className="project-page-grid">
            <main className="project-main-column">
              <section className="project-panel project-panel--object">
                <Typography variant="h6">Objet {isIntervention ? "de l’intervention" : "du projet"}</Typography>
                <p>{project.description || '-'}</p>
              </section>

              {/* <section className="project-panel">
                <Typography variant="h6">Arborescence technique</Typography>
                <div className="project-field-list">
                  {nodes.map((node) => <div key={node.id} style={{ paddingLeft: `${Number(node.depth || 0) * 20}px`, marginBottom: 8 }}>
                    <strong>{node.code}</strong> — {node.name} <small>({node.nodeType})</small>
                  </div>)}
                </div>
                <div className="project-creation-grid" style={{ marginTop: 16 }}>
                  <Input fullWidth label="Code du nœud" value={nodeForm.code} onChange={(event) => setNodeForm((value) => ({ ...value, code: event.target.value.toUpperCase() }))} />
                  <Input fullWidth label="Nom du périmètre" value={nodeForm.name} onChange={(event) => setNodeForm((value) => ({ ...value, name: event.target.value }))} />
                  <div className="custom-input custom-input--full"><label className="custom-input__label" htmlFor="node-parent">Parent</label><select id="node-parent" className="custom-input__field" value={nodeForm.parentNodeId} onChange={(event) => setNodeForm((value) => ({ ...value, parentNodeId: event.target.value }))}><option value="">Racine</option>{nodes.map((node) => <option key={node.id} value={node.id}>{node.code} — {node.name}</option>)}</select></div>
                  <div className="custom-input custom-input--full"><label className="custom-input__label" htmlFor="node-type">Type</label><select id="node-type" className="custom-input__field" value={nodeForm.nodeType} onChange={(event) => setNodeForm((value) => ({ ...value, nodeType: event.target.value }))}>{['building', 'station', 'zone', 'level', 'technical_room', 'phase', 'custom'].map((type) => <option key={type} value={type}>{type}</option>)}</select></div>
                </div>
                <button className="basic-button" type="button" onClick={handleAddNode} disabled={addingNode}>{addingNode ? 'Création...' : 'Ajouter un périmètre'}</button>
              </section> */}

              <section className="project-panel">
                <Typography variant="h6">Planning</Typography>
                <div className="project-timeline">
                  <div className="project-timeline-row">
                    <span>{realizationLabel}</span>
                    <strong>{formatDate(getValue(project, 'startDate', 'start_date'))}</strong>
                    <em>{formatDate(getValue(project, 'endDate', 'end_date'))}</em>
                  </div>
                </div>
              </section>

              {/* <ProjectLdaOverview project={project} /> */}

              <section className="project-panel project-panel--traceability">
                <ProjectPlanningTraceability projectId={projectId} />
              </section>

              <section className="project-panel">
                <Typography variant="h6">Adresse du site</Typography>
                <dl className="project-field-list">
                  <FieldRow label="Adresse" value={getValue(project, 'address_line1', 'addressLine1')} />
                  <FieldRow label="Complement" value={getValue(project, 'address_line2', 'addressLine2')} />
                  <FieldRow label="Code postal" value={getValue(project, 'postal_code', 'postalCode')} />
                  <FieldRow label="Ville" value={project.city} />
                  <FieldRow label="Region / Etat" value={getValue(project, 'region_or_state', 'regionOrState')} />
                  <FieldRow label="Pays" value={getValue(project, 'country_code', 'countryCode')} />
                </dl>
              </section>

              <section className="project-panel">
                <Typography variant="h6">Phases et gestion</Typography>
                <dl className="project-field-list">
                  <FieldRow label="Phases" value={selectedPhases.join(', ')} />
                  <FieldRow label="Gestion" value={selectedManagement.join(', ')} />
                </dl>
              </section>
            </main>

            <aside className="project-side-column">
              <section className="project-panel project-progress-card">
                <div className="project-progress-card__header">
                  <Typography variant="h6">Avancement</Typography>
                  <strong>{progress}%</strong>
                </div>
                <LinearProgress
                  variant="determinate"
                  value={progress}
                  sx={{
                    height: 8,
                    borderRadius: 4,
                    backgroundColor: 'var(--erp-surface-4)',
                    '& .MuiLinearProgress-bar': { backgroundColor: 'var(--erp-primary)' },
                  }}
                />
              </section>

              <section className="project-panel">
                <Typography variant="h6">Données client</Typography>
                <dl className="project-field-list project-field-list--compact">
                  <FieldRow label="Raison sociale" value={getClientName(client)} />
                  <FieldRow label="Téléphone" value={client?.phone} />
                  <FieldRow label="Email" value={client?.email} />
                </dl>
              </section>

              <section className="project-panel">
                <Typography variant="h6">Budget</Typography>
                <dl className="project-field-list project-field-list--compact">
                  <FieldRow label="Budget" value={formatMoney(project.budget)} />
                  <FieldRow label="Vendu" value={formatMoney(getValue(project, 'soldAmount', 'sold_amount'))} />
                  <FieldRow label="Coût estimé" value={formatMoney(getValue(project, 'estimatedCost', 'estimated_cost'))} />
                </dl>
              </section>

              <section className="project-panel">
                <Typography variant="h6">Complements</Typography>
                <dl className="project-field-list project-field-list--compact">
                  <FieldRow label="Batiment" value={getValue(project, 'buildingType', 'building_type') || complements.buildingType} />
                  <FieldRow label={isIntervention ? "Mode de réalisation" : "Type projet"} value={isIntervention ? 'Intervention' : getValue(project, 'projectType', 'project_type')} />
                  <FieldRow label="Surface" value={formatSurface(project.surface || complements.surface)} />
                  <FieldRow label="Travaux" value={getValue(project, 'workType', 'work_type') || complements.workType} />
                </dl>
              </section>

              <section className="project-panel">
                <Typography variant="h6">Acces rapides</Typography>
                <div className="project-quick-links">
                  <Link to={`/projets/${projectId}/bilan-puissance`}>Bilan de puissance</Link>
                  <Link to={`/projets/${projectId}/materiel`}>Suivi de matériel</Link>
                  <Link to={`${realizationBasePath}/${projectId}/lda`}>Liste des documents attendus</Link>
                  <Link to={`/projets/${projectId}/devis`}>Devis liés à la réalisation</Link>
                  <Link to={`/saisietemps/${projectId}`}>Saisie des temps</Link>
                  <Link to={`/projets/${projectId}/materials`}>Materiel et reservations</Link>
                </div>
              </section>
            </aside>
          </div>

          {renderEditModal()}
        </>
      )}
    </Box>
  );
}

export default ProjectPage;
